import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { PrivacyAndCookieService } from "../../../services/PrivacyCookiesService";
import { ILanguage, IPrivacyNotice } from "../../../models/Interfaces";
import { ContentService } from "../../../services/ContentService";
import { useConfirm } from "../../../config/ConfirmProvider";
import { Button, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCookie, faEdit, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Column, CellProps } from 'react-table';
import Loading from "../../components/common/Loading";
import AddUpdatePrivacyNoticeModal from "./AddUpdatePrivacyNoticeModal";
import FilteredTable from "../../components/common/FilteredTable";
import styles from "./privacyNotices.module.scss";


const PrivacyNotices = () => {
  const [privacyNotices, setPrivacyNotices] = useState<Array<IPrivacyNotice>>([]);
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedNotice, setSelectedNotice] = useState<IPrivacyNotice | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  const cookieSvcRef = useRef<PrivacyAndCookieService | null>(null);
  const contentSvcRef = useRef<ContentService>();
  const confirmation = useConfirm();

  const columns: Column<IPrivacyNotice>[] = useMemo(() => [
    {
      id: "Language",
      Header: "Language",
      Cell: ({ row }: CellProps<IPrivacyNotice>) => (
        <>{getLanguage(row.original.Language ?? "")}</>
      )
    },
    {
      Header: "Position",
      accessor: "Location"
    },
    {
      Header: "Enabled",
      Cell: ({ row }: CellProps<IPrivacyNotice>) => {
        if (row.original.Enabled) {
          return (
            <span className={`${styles.statusBadge} ${styles.statusEnabled}`} title="Enabled">
              <FontAwesomeIcon icon={faCheck} />
            </span>
          );
        } else {
          return (
            <span className={`${styles.statusBadge} ${styles.statusDisabled}`} title="Disabled">
              <FontAwesomeIcon icon={faTimes} />
            </span>
          );
        }
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<IPrivacyNotice>) => (
        <div className="user-result-actions user-result-actions--single">
          <Button variant="light" onClick={() => editNotice(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="edit privacy notice">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteNotice(row.original.Id)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete privacy notice">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [languages, privacyNotices]);

  const getCookieNotices = async (): Promise<void> => {
    if (cookieSvcRef.current) {
      const res = await cookieSvcRef.current.getPrivacyNotices(false);

      if (res) {
        setPrivacyNotices(res);
      }
    }
  };

  const getLanguages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const langs = await contentSvcRef.current.getSiteLanguages();
      if (langs) {
        setLanguages(langs);
      }
    }
  };

  const getLanguage = (shortcode: string): string => {
    return languages?.find(l => l.ShortCode === shortcode)?.Language ?? shortcode;
  };

  const addNotice = (): void => {
    if (cookieSvcRef.current) {
      const newnotice = cookieSvcRef.current.newPrivacyNotice();
      if (newnotice) {
        setSelectedNotice(newnotice);
        setShowModal(true);
      }
    }
  };

  const editNotice = (notice: IPrivacyNotice): void => {
    if (notice) {
      setSelectedNotice(notice);
      setShowModal(true);
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedNotice, (item) => item.Id, () => closeDetails());

  const deleteNotice = async (noticeid: number): Promise<void> => {
    if (cookieSvcRef.current) {
      const confirmed = await confirmation.requestConfirmation("Delete this privacy notice? <b>This action cannot be undone.</b>", "Delete Privacy Notice");

      if (confirmed) {
        const delres = await cookieSvcRef.current.deletePrivacyNotice(noticeid);

        if (delres) {
          const filtered = privacyNotices.filter(pn => pn.Id !== noticeid);
          setPrivacyNotices(filtered);
          closeDeletedSelection(noticeid);
        }
      }
    }
  };

  const saveNotice = async (notice: IPrivacyNotice): Promise<void> => {
    if (cookieSvcRef.current && notice) {
      const res = await cookieSvcRef.current.savePrivacyNotice(notice);

      if (res) {
        const existing = privacyNotices?.find(pn => pn.Id === res.Id);

        if (existing) {
          const updated = privacyNotices.map(pn => pn.Id === res.Id ? res : pn);
          setPrivacyNotices(updated);
        } else {
          setPrivacyNotices(prev => ([
            ...prev,
            res
          ]));
        }
        closeDetails();
      }
    }
  };

  const closeDetails = (): void => {
    setSelectedNotice(null);
    setShowModal(false);
  };

  useEffect(() => {
    let active = true;
    contentSvcRef.current ??= new ContentService();
    cookieSvcRef.current ??= new PrivacyAndCookieService();
    setLoading(true);
    void Promise.allSettled([getLanguages(), getCookieNotices()]).then(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, []);

  return (
    <>
      {loading && (
        <Row>
          <Col md={12} className="text-center mt-5">
            <Loading Message="loading available privacy notices..." SpinSize="5x" key={"LoadingSpinner"}></Loading>
          </Col>
        </Row>
      )}

      {!loading && languages?.length > 0 && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div>
                <h2 className="app-list-section__title">Notice Library</h2>
              </div>
            </div>
            <div className="app-list-section__body">
            <FilteredTable<IPrivacyNotice>
              columns={columns}
              data={privacyNotices}
              noDataMessage="No site privacy notices found, click the add button above to add a new privacy notice"
              addButtonHandler={addNotice}
              addButtonText="Add a privacy notice"
              addButtonIcon={faCookie}
              showAddButton={true}
              filterPlaceHolder="filter privacy notices..."
            />
            </div>
          </section>
        </div>
      )}

      {!loading && selectedNotice && showModal && (
        <AddUpdatePrivacyNoticeModal
          notice={selectedNotice}
          onClose={closeDetails}
          onSave={saveNotice}
          key={"AddUpdatePrivacyNoticeModal"}
          languages={languages ?? []}
        />
      )}
    </>
  );
}

export default PrivacyNotices;
